/**
 * Exports a React Component for rendering images.
 * Images can either be given absolute width or height
 * or be scaled intrinsically.
 * Example use:
 * <ImageLoader src='**.jpg' width='30%' hasShadow={true} ratio='16:9' />
 *
 * @module Views/ImageView/ImageView
 */

import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import ReactCSSTransitionGroup from 'react-transition-group/CSSTransitionGroup';

import Img from '../../components/ImageLoader/Image';
import * as Utils from '../../utils';

import './ImageLoader.scss';

const IMAGERATIOS = {
  '16:9': 'ImageLoader--ratio_16_9',
  '1:1': 'ImageLoader--ratio_1_1',
};

const DEFAULT_BG_COLOR = 'rgba(0, 0, 0, 0.05)';

export default class extends React.PureComponent {
  /**
   * @property {object} props - the properties used to initialize the component.
   * @property {string} props.src - the source url for the image. If not set, no image will load.
   * @property {string} props.alt - Alt text used by non-visual browsers.  Required for accessibility.
   * @property {string} props.role - An aria role, optionally used for accessibility.
   * @property {string} props.height - the height string (i.e '100px', '100%', etc.) to be used
   *                                   to assign the height style of the image. If no height is specified,
   *                                   the image will be intrinsically sized.
   * @property {string} props.width - the width string (i.e '100px', '100%', etc.) to be used
   *                                   to assign the width style of the image. If no width is specified,
   *                                   the image will be intrinsically sized.
   * @property {boolean} props.hasShadow - whether to give the image a box shadow style.
   * @property {boolean} props.doesFade - whether to fade the image in on load.
   * @property {string} props.ratio - a string used to assign the aspect ratio of an image
   *                                  which is intrinsically sized.
   * @property {bgClass} props.bgClass - css class(es) string that will be applied to the background layer
   *                                     behind the image.
   * @property {bgColor} props.bgColor - Full color string (e.g. hex or rgba) that will be applied to the
   *                                     background of the background layer behind the image.
   * @property {useDefaultBgColor} props.useDefaultBgColor - Sets bgColor to a default color, generally
   *                                                         used for a loading bg color
   */
  static propTypes = {
    src: PropTypes.string,
    alt: PropTypes.string,
    role: PropTypes.oneOf(Utils.Accessibility.AriaRolesArray),
    replacementImg: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    height: PropTypes.string,
    width: PropTypes.string,
    hasShadow: PropTypes.bool,
    hasBorder: PropTypes.bool,
    doesFade: PropTypes.bool,
    ratio: PropTypes.string,
    qaTag: PropTypes.string,
    hasCover: PropTypes.bool,
    isCircle: PropTypes.bool,
    imgClass: PropTypes.string,
    bgClass: PropTypes.string,
    bgColor: PropTypes.string,
    useDefaultBgColor: PropTypes.bool,
    onLoad: PropTypes.func,
  };

  static defaultProps = {
    doesFade: true,
    hasShadow: true,
    ratio: '1:1',
    hasCover: true,
    replacementImg: Utils.Art.getDefaultArt(),
  };

  constructor(props) {
    super(props);
    // Check if browser supports 'objectFit' in order to use cover effect on an <img> tag (currently not supported by IE & Edge)
    const objectFitSupported = 'objectFit' in document.documentElement.style;

    this.state = {
      loaded: false,
      objectFitSupported,
    };
  }

  onImageLoad = event => {
    this.setState({
      loaded: true,
    });
    if (this.props.onLoad) {
      this.props.onLoad(event);
    }
  };

  renderImage = (imgClasses, bgClasses) => {
    const imageStyles = {
      height: this.props.height,
      width: this.props.width,
    };
    return (
      <React.Fragment key={this.props.src}>
        {this.renderBg(bgClasses)}
        {this.props.src && (
          <Img
            replacementImg={this.props.replacementImg}
            onLoadFunc={this.onImageLoad}
            styles={imageStyles}
            classes={imgClasses}
            alt={this.props.alt}
            src={this.props.src}
            role={this.props.role}
            qaTag={this.props.qaTag}
          />
        )}
      </React.Fragment>
    );
  };

  renderImageAsBg = imgClasses => {
    const imageStyles = {
      backgroundImage: `url("${this.props.src ? this.props.src : this.props.replacementImg}")`,
      paddingBottom: this.props.height,
      width: this.props.width,
    };
    return (
      <div className={imgClasses} style={imageStyles} key={this.props.src}>
        {this.renderBg()}
        <Img
          classes='ImageLoader__bgImg__hiddenImg'
          replacementImg={this.props.replacementImg}
          onLoadFunc={this.onImageLoad}
          alt={this.props.alt}
          src={this.props.src}
          role={this.props.role}
          qaTag={this.props.qaTag}
        />
      </div>
    );
  };

  renderBg = bgClasses => {
    if (this.props.bgColor || this.props.useDefaultBgColor) {
      const bgStyles = {
        height: this.props.height,
        width: this.props.width,
        backgroundColor: this.props.bgColor || (this.props.useDefaultBgColor && DEFAULT_BG_COLOR),
      };
      return <div style={bgStyles} className={bgClasses} />;
    }
  };

  render() {
    const useIntrinsicSizing = !this.props.height || !this.props.width;
    const componentClasses = classNames({
      ImageLoader: true,
      'ImageLoader--scaling': useIntrinsicSizing && this.props.ratio,
      [IMAGERATIOS[this.props.ratio]]: useIntrinsicSizing && this.props.ratio,
    });
    const imgClasses = classNames(this.props.imgClass, {
      ImageLoader__shadow: this.props.hasShadow,
      ImageLoader__border: this.props.hasBorder,
      ImageLoader__intrinsic: useIntrinsicSizing,
      ImageLoader__loaded: this.state.loaded,
      ImageLoader__cover: this.props.hasCover && this.state.objectFitSupported,
      ImageLoader__bgImg: !this.state.objectFitSupported,
      ImageLoader__circle: this.props.isCircle,
    });
    const bgClasses = classNames(this.props.bgClass, {
      ImageLoader__intrinsic: useIntrinsicSizing,
      ImageLoader__circle: this.props.isCircle,
    });
    return (
      <ReactCSSTransitionGroup
        className={componentClasses}
        transitionName='fx-imageLoader'
        transitionAppear={this.props.doesFade}
        transitionAppearTimeout={250}
        transitionEnterTimeout={250}
        transitionLeaveTimeout={250}
        component='div'
      >
        {!this.state.objectFitSupported && this.state.loaded
          ? this.renderImageAsBg(imgClasses, bgClasses)
          : this.renderImage(imgClasses, bgClasses)}
      </ReactCSSTransitionGroup>
    );
  }
}
